import { useEffect, useState, useRef } from "react";
import { loadModules, setDefaultOptions } from "esri-loader";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import SelectYear from "./SelectYear";
import SelectSemester from "./SelectSemester";
import SelectDepto from "./SelectDepto";
import SelectMpio from "./SelectMpio";
import ProvinceInfo from "./ProvinceInfo";
import DownloadButtons from "./DownloadButtons";
import ApplyFilterButtons from "./ApplyFilterButtons";
import FileReport from "./FileReport";
import {
    generarNombreArchivo,
    generarUrlArchivo,
    verificarArchivoExiste,
} from "./utils";
import HelpModal from "./HelpModal";
import DownloadUnificadoButton from "./DownloadUnificadoButton";
import DescargaRecomendada from "./DescargaRecomendada";
import LoadingOverlay from "./LoadingOverlay/LoadingOverlay";

import "./styles.css";

function FilterAPU() {
    setDefaultOptions({ version: "3.38" });
    const fileReportRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState("");
    const [isValidatingMode, setIsValidatingMode] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [selectedYear, setSelectedYear] = useState("2026");
    const [selectedSemester, setSelectedSemester] = useState("1");
    const [selectedDepto, setSelectedDepto] = useState("-1");
    const [selectedMpio, setSelectedMpio] = useState("-1");
    const [provinciaInfo, setProvinciaInfo] = useState(null);
    const [disabledDownloadFileProv, setEnableDownloadFileProv] =
        useState(true);
    const [disabledDownloadFileDepto, setEnableDownloadFileDepto] =
        useState(true);
    const [fileReportVisible, setFileReportVisible] = useState(false);
    const [archivoFaltantes, setArchivoFaltantes] = useState([]);
    const [todosArchivosOk, setTodosArchivosOk] = useState(false);
    const [mensajeReporte, setMensajeReporte] = useState(
        "Consultando archivos..."
    );
    const [mensajeLoadingDownload, setMensajeLoadingDownload] = useState("");

    const [showHelp, setShowHelp] = useState(false);
    const handleCloseHelp = () => setShowHelp(false);
    const handleShowHelp = () => setShowHelp(true);

    const mostrarResultado = () => {
        setFileReportVisible(true);
        setTimeout(() => {
            fileReportRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 200);
    };

    // Validar existencia de archivo ZIP único a nivel país
    const handleApplyFilterCountry = async () => {
        setIsDownloading(false);
        setFileReportVisible(false);
        setMensajeReporte("");

        const year = selectedYear;
        const semester = selectedSemester;
        const nombreArchivo = `${year}_${semester}.zip`;
        const url =
            process.env.REACT_APP_HERMES2_URL +
            process.env.REACT_APP_FOLDER_PROVINCIAS_URL +
            `${nombreArchivo}`;

        const existe = await verificarArchivoExiste(url);

        if (existe) {
            setMensajeReporte(
                "Archivo a nivel país (.zip) encontrado. Se encuentra disponible en el servidor."
            );
            setTodosArchivosOk(true);
            setArchivoFaltantes([]);
            mostrarResultado();
            return url;
        } else {
            setMensajeReporte(
                "No existe un archivo a nivel país (.zip) para el año y semestre seleccionados."
            );
            setTodosArchivosOk(false);
            setArchivoFaltantes([nombreArchivo]);
            mostrarResultado();
            return null;
        }
    };

    // Validar existencia de archivos de provincias para el departamento seleccionado usando Capa 3 (Datos APU)
    const handleApplyFilterDepto = async () => {
        setIsDownloading(false);
        if (!selectedDepto || selectedDepto === "-1") {
            setMensajeReporte("Debe seleccionar un departamento");
            setTodosArchivosOk(false);
            setArchivoFaltantes([]);
            mostrarResultado();
            return [];
        }

        setLoading(true);
        setMensajeLoadingDownload("Consultando provincias del departamento...");
        setFileReportVisible(false);
        setArchivoFaltantes([]);
        setTodosArchivosOk(false);

        try {
            const [Query, QueryTask] = await loadModules([
                "esri/tasks/query",
                "esri/tasks/QueryTask",
            ]);

            // Capa 3: Materiales (Contiene los datos y nombres exactos de provincia de APU)
            let queryTask = new QueryTask(
                process.env.REACT_APP_HERMES2_URL +
                    process.env.REACT_APP_APU_SERVICE_URL +
                    "3"
            );

            let query = new Query();
            query.where = `codigodepartamento = '${selectedDepto}'`;
            query.returnGeometry = false;
            query.outFields = ["*"];

            let results = await queryTask.execute(query);

            // Deduplicar provincias por código de provincia
            const uniqueProvincesMap = new Map();
            for (const feature of results.features) {
                const attrs = feature.attributes;
                const codProv = attrs.codigoprovincia || attrs.cod_provincia;
                if (codProv && !uniqueProvincesMap.has(codProv)) {
                    uniqueProvincesMap.set(codProv, attrs);
                }
            }

            const provinciasUnicas = Array.from(uniqueProvincesMap.values());
            const urlsEncontradas = [];
            const archivosNoEncontrados = [];

            const fileChecks = provinciasUnicas.map((attrs) => {
                const url = generarUrlArchivo(
                    attrs,
                    selectedYear,
                    selectedSemester
                );
                const nombre = generarNombreArchivo(
                    attrs,
                    selectedYear,
                    selectedSemester
                );
                return { url, nombre };
            });

            for (let i = 0; i < fileChecks.length; i += 5) {
                const chunk = fileChecks.slice(i, i + 5);
                await Promise.all(
                    chunk.map(async (item) => {
                        const existe = await verificarArchivoExiste(item.url);
                        if (existe) {
                            urlsEncontradas.push({ name: item.nombre, url: item.url });
                        } else {
                            archivosNoEncontrados.push(item.nombre);
                        }
                    })
                );
            }

            setArchivoFaltantes(archivosNoEncontrados);
            setTodosArchivosOk(archivosNoEncontrados.length === 0);
            if (archivosNoEncontrados.length === 0) {
                setMensajeReporte(
                    `Todos los archivos del departamento seleccionado (${urlsEncontradas.length}) existen en el servidor:`
                );
            } else {
                setMensajeReporte(
                    `Se encontraron ${archivosNoEncontrados.length} archivo(s) faltante(s) de un total de ${fileChecks.length} provincia(s) en el departamento:`
                );
            }
            mostrarResultado();

            return urlsEncontradas;
        } catch (error) {
            console.error("Error al consultar provincias del departamento:", error);
            setMensajeReporte("Error consultando provincias del departamento.");
            mostrarResultado();
            return [];
        } finally {
            setLoading(false);
        }
    };

    // Validar existencia de archivo para el municipio / provincia seleccionada
    const handleApplyFilterProv = async () => {
        setIsDownloading(false);
        setFileReportVisible(false);
        setMensajeReporte("");

        if (!selectedDepto || selectedDepto === "-1") {
            setMensajeReporte("Debe seleccionar un departamento");
            setTodosArchivosOk(false);
            setArchivoFaltantes([]);
            mostrarResultado();
            return null;
        }

        if (!selectedMpio || selectedMpio === "-1") {
            setMensajeReporte("Debe seleccionar un municipio");
            setTodosArchivosOk(false);
            setArchivoFaltantes([]);
            mostrarResultado();
            return null;
        }

        setLoading(true);
        setMensajeLoadingDownload("Consultando provincia...");

        try {
            const [Query, QueryTask] = await loadModules([
                "esri/tasks/query",
                "esri/tasks/QueryTask",
            ]);

            let queryTask = new QueryTask(
                process.env.REACT_APP_HERMES2_URL +
                    process.env.REACT_APP_APU_SERVICE_URL +
                    "3"
            );

            let query = new Query();
            query.where = `codigodepartamento = '${selectedDepto}'`;
            query.returnGeometry = false;
            query.outFields = ["*"];

            let results = await queryTask.execute(query);

            let provinciaEncontrada = null;

            for (const feature of results.features) {
                const attrs = feature.attributes;
                const codMpios = (attrs.cod_municipios || "")
                    .split(",")
                    .map((m) => m.trim());

                if (codMpios.includes(selectedMpio.trim())) {
                    provinciaEncontrada = attrs;
                    break;
                }
            }

            if (!provinciaEncontrada && results.features.length > 0) {
                provinciaEncontrada = results.features[0].attributes;
            }

            if (provinciaEncontrada) {
                setProvinciaInfo(provinciaEncontrada);

                const url = generarUrlArchivo(
                    provinciaEncontrada,
                    selectedYear,
                    selectedSemester
                );
                const existe = await verificarArchivoExiste(url);

                if (existe) {
                    setMensajeReporte("Archivo de la provincia encontrado en el servidor.");
                    setTodosArchivosOk(true);
                    setArchivoFaltantes([]);
                    mostrarResultado();
                    return url;
                } else {
                    setMensajeReporte(
                        "No existe un archivo para el año, semestre y provincia seleccionados:"
                    );
                    setArchivoFaltantes([
                        generarNombreArchivo(
                            provinciaEncontrada,
                            selectedYear,
                            selectedSemester
                        ),
                    ]);
                    setTodosArchivosOk(false);
                    mostrarResultado();
                    return null;
                }
            } else {
                setProvinciaInfo(null);
                setMensajeReporte(
                    "No se encontró provincia para el municipio seleccionado."
                );
                setArchivoFaltantes([]);
                setTodosArchivosOk(false);
                mostrarResultado();
                return null;
            }
        } catch (error) {
            console.error("Error en consulta de provincias:", error);
            setMensajeReporte("Error consultando provincia.");
            mostrarResultado();
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Validar masivamente la totalidad de provincias de Colombia por Año y Periodo desde Capa 3 (Datos APU)
    const handleApplyFilterAllCountryProvinces = async () => {
        setIsDownloading(false);
        setLoading(true);
        setMensajeLoadingDownload("Consultando la totalidad de provincias de Colombia...");
        setFileReportVisible(false);
        setArchivoFaltantes([]);
        setTodosArchivosOk(false);

        try {
            const [Query, QueryTask] = await loadModules([
                "esri/tasks/query",
                "esri/tasks/QueryTask",
            ]);

            // Capa 3: Materiales (Provincias oficiales con nombres de APU)
            let queryTask = new QueryTask(
                process.env.REACT_APP_HERMES2_URL +
                    process.env.REACT_APP_APU_SERVICE_URL +
                    "3"
            );

            let query = new Query();
            query.where = "1=1";
            query.returnGeometry = false;
            query.outFields = ["*"];

            let results = await queryTask.execute(query);

            // Deduplicar provincias por código de provincia
            const uniqueProvincesMap = new Map();
            for (const feature of results.features) {
                const attrs = feature.attributes;
                const codProv = attrs.codigoprovincia || attrs.cod_provincia;
                if (codProv && !uniqueProvincesMap.has(codProv)) {
                    uniqueProvincesMap.set(codProv, attrs);
                }
            }

            const provinciasUnicas = Array.from(uniqueProvincesMap.values());
            const archivosNoEncontrados = [];

            const fileChecks = provinciasUnicas.map((attrs) => {
                const url = generarUrlArchivo(
                    attrs,
                    selectedYear,
                    selectedSemester
                );
                const nombre = generarNombreArchivo(
                    attrs,
                    selectedYear,
                    selectedSemester
                );
                return { url, nombre };
            });

            for (let i = 0; i < fileChecks.length; i += 5) {
                const chunk = fileChecks.slice(i, i + 5);
                await Promise.all(
                    chunk.map(async (item) => {
                        const existe = await verificarArchivoExiste(item.url);
                        if (!existe) {
                            archivosNoEncontrados.push(item.nombre);
                        }
                    })
                );
            }

            setArchivoFaltantes(archivosNoEncontrados);
            setTodosArchivosOk(archivosNoEncontrados.length === 0);
            if (archivosNoEncontrados.length === 0) {
                setMensajeReporte(
                    `¡Excelente! Todos los archivos de provincias a nivel nacional (${fileChecks.length}) existen en el servidor para el periodo ${selectedYear}-${selectedSemester}.`
                );
            } else {
                setMensajeReporte(
                    `Reporte Nacional: Se encontraron ${archivosNoEncontrados.length} archivo(s) faltante(s) de un total de ${fileChecks.length} provincia(s) para el periodo ${selectedYear}-${selectedSemester}:`
                );
            }
            mostrarResultado();
        } catch (error) {
            console.error("Error al consultar todas las provincias del país:", error);
            setMensajeReporte("Error realizando la consulta nacional de provincias.");
            mostrarResultado();
        } finally {
            setLoading(false);
        }
    };

    // Descarga a nivel País
    const handleDownloadCountryWithConsulta = async () => {
        setIsDownloading(true);
        setLoading(true);
        setMensajeLoadingDownload("Consultando APU a nivel país...");
        const url = await handleApplyFilterCountry();

        if (!url) {
            setLoading(false);
            return;
        }
        setMensajeLoadingDownload("Descargando APU a nivel país...");

        const link = document.createElement("a");
        link.href = url;
        link.download = "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
    };

    // Auxiliar para descarga por bloques
    const fetchInChunks = async (files, maxConcurrent = 3) => {
        const results = [];

        for (let i = 0; i < files.length; i += maxConcurrent) {
            const chunk = files.slice(i, i + maxConcurrent);
            const responses = await Promise.all(
                chunk.map(async (file) => {
                    const response = await fetch(file.url);
                    const blob = await response.blob();
                    return { name: file.name, blob };
                })
            );
            results.push(...responses);
        }

        return results;
    };

    // Descarga por Departamento
    const handleDownloadDeptoWithConsulta = async () => {
        setIsDownloading(true);
        setLoading(true);
        setMensajeLoadingDownload(
            "Consultando APU del departamento seleccionado..."
        );
        const archivos = await handleApplyFilterDepto();

        if (archivos.length === 0) {
            setLoading(false);
            return;
        }
        setMensajeLoadingDownload(
            "Descargando APU del departamento seleccionado..."
        );

        const zip = new JSZip();

        try {
            const files = await fetchInChunks(archivos, 3);

            files.forEach(({ name, blob }) => {
                zip.file(name, blob);
            });

            const selectedDeptoText =
                selectedDepto !== "-1"
                    ? document
                          .querySelector("#deptos option:checked")
                          ?.textContent?.trim()
                    : "Departamento";

            const blobZip = await zip.generateAsync({ type: "blob" });
            saveAs(blobZip, `APU_${selectedDeptoText}.zip`);
        } catch (error) {
            console.error("Error al generar ZIP:", error);
            alert("Ocurrió un error al generar el archivo ZIP.");
        } finally {
            setLoading(false);
        }
    };

    // Descarga por Provincia / Municipio
    const handleDownloadProvWithConsulta = async () => {
        setIsDownloading(true);
        setLoading(true);
        setMensajeLoadingDownload(
            "Consultando APU del municipio seleccionado..."
        );
        const url = await handleApplyFilterProv();

        if (!url) {
            setLoading(false);
            return;
        }
        setMensajeLoadingDownload(
            "Descargando APU del municipio seleccionado..."
        );

        const link = document.createElement("a");
        link.href = url;
        link.download = "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
    };

    useEffect(() => {
        const isYearPeriodValid = parseInt(selectedYear) > 2021 || (parseInt(selectedYear) === 2021 && parseInt(selectedSemester) >= 2);
        if (selectedDepto !== "-1" && isYearPeriodValid) {
            setEnableDownloadFileDepto(false);
        } else {
            setEnableDownloadFileDepto(true);
        }
    }, [selectedDepto, selectedYear, selectedSemester]);

    useEffect(() => {
        setProvinciaInfo(null);
        setMensajeReporte("");
        setFileReportVisible(false);
    }, [selectedMpio]);

    useEffect(() => {
        const isYearPeriodValid = parseInt(selectedYear) > 2021 || (parseInt(selectedYear) === 2021 && parseInt(selectedSemester) >= 2);
        if (selectedMpio !== "-1" && selectedDepto !== "-1" && isYearPeriodValid) {
            setEnableDownloadFileProv(false);
        } else {
            setEnableDownloadFileProv(true);
        }
    }, [selectedMpio, selectedDepto, selectedYear, selectedSemester]);

    useEffect(() => {
        const params = new URL(window.location.href).searchParams;
        const validarArchivos = params.get("validarArchivos") === "true";
        setIsValidatingMode(validarArchivos);
        const dynamicTitle = validarArchivos
            ? "CONSULTE LA EXISTENCIA DE LOS ARCHIVOS DE APU DE REFERENCIA EN EL SERVIDOR"
            : "DESCARGAR ARCHIVO DE APU DE REFERENCIA";
        setTitle(dynamicTitle);
    }, []);

    return (
        <div className="App container mb-4">
            {loading && <LoadingOverlay mensaje={mensajeLoadingDownload} />}
            <h3 className="mt-4 font-mpb text-center">{title}</h3>
            <div>
                <h3 className="titles-form mb-1 font-mpb">
                    Opciones de consulta (
                    <button
                        type="button"
                        id="btn-help"
                        onClick={handleShowHelp}
                        className="btn btn-link p-0 align-baseline"
                        style={{
                            fontSize: "inherit",
                            textDecoration: "underline",
                        }}
                    >
                        Ayuda
                    </button>
                    ):
                </h3>
                <p>
                    Según la alternativa de consulta de información de su
                    interés, seleccione los datos correspondientes:
                </p>

                <div id="cont-depmpio" className="row m-0">
                    <SelectDepto
                        selectedDepto={selectedDepto}
                        setSelectedDepto={setSelectedDepto}
                        setSelectedMpio={setSelectedMpio}
                    />
                    <SelectMpio
                        selectedDepto={selectedDepto}
                        selectedMpio={selectedMpio}
                        setSelectedMpio={setSelectedMpio}
                    />
                    <SelectYear
                        selectedYear={selectedYear}
                        setSelectedYear={setSelectedYear}
                        setSelectedSemester={setSelectedSemester}
                    />
                    <SelectSemester
                        selectedYear={selectedYear}
                        selectedSemester={selectedSemester}
                        setSelectedSemester={setSelectedSemester}
                    />
                </div>

                <ProvinceInfo provinciaInfo={provinciaInfo} />

                {isValidatingMode && (
                    <ApplyFilterButtons
                        onApplyFilterProv={handleApplyFilterProv}
                        onApplyFilterDepto={handleApplyFilterDepto}
                        onApplyFilterAllCountry={handleApplyFilterAllCountryProvinces}
                        onApplyFilterCountryZip={handleApplyFilterCountry}
                        disabledProv={disabledDownloadFileProv}
                        disabledDepto={disabledDownloadFileDepto}
                    />
                )}

                <FileReport
                    ref={fileReportRef}
                    visible={fileReportVisible}
                    archivosFaltantes={archivoFaltantes}
                    todosEncontrados={todosArchivosOk}
                    mensaje={mensajeReporte}
                    isDownloading={isDownloading}
                />

                <DescargaRecomendada
                    selectedYear={selectedYear}
                    tipo="arriba"
                />

                <DownloadButtons
                    onDownloadCountry={handleDownloadCountryWithConsulta}
                    onDownloadDepto={handleDownloadDeptoWithConsulta}
                    onDownloadProv={handleDownloadProvWithConsulta}
                    disabledProv={disabledDownloadFileProv}
                    disabledDepto={disabledDownloadFileDepto}
                    disabledCountry={false}
                />

                <DescargaRecomendada selectedYear={selectedYear} tipo="abajo" />
                <DownloadUnificadoButton selectedYear={selectedYear} selectedSemester={selectedSemester} />

                <HelpModal show={showHelp} onClose={handleCloseHelp} />
            </div>
        </div>
    );
}

export default FilterAPU;
